import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { MessageSquarePlus, FileText } from 'lucide-react';
import { hideAddIncidentCommentModal } from '@@/features/modal';
import { useAddIncidentCommentMutation } from '@@/services/incident';
import { useGetTemplateMessagesQuery } from '@@/services/templateMessage';
import { RootState } from '@@/store';
import { isEmbedded } from '@@/utils/embed';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import StandardInput from '@@/components/Inputs/StandardInput';
import SelectInput from '@@/components/Inputs/SelectInput';

const schema = yup
  .object({
    body: yup.string().trim().required('Entrez votre commentaire'),
  })
  .required();

const AddIncidentCommentModal: React.FC<{ incidentId: string }> = ({
  incidentId,
}) => {
  const dispatch = useDispatch();
  const showAddIncidentCommentModal = useSelector(
    (state: RootState) => state.modal.showAddIncidentCommentModal,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm<{
    body: string;
    template?: string;
  }>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const closeModal = useCallback(() => {
    dispatch(hideAddIncidentCommentModal());
    reset();
  }, [dispatch, reset]);

  // Les messages types sortent du périmètre incidents : en embed on n'expose
  // pas la bibliothèque interne du partenaire, et un token restreint aux
  // incidents ferait tomber la session sur le 401 de cet appel.
  const { data: templateMessages } = useGetTemplateMessagesQuery(undefined, {
    skip: isEmbedded(),
  });
  const [addIncidentComment, { isSuccess }] = useAddIncidentCommentMutation();

  const onSubmit = useCallback(
    (data: { body: string }) => {
      if (incidentId) {
        addIncidentComment({
          incidentId,
          body: data.body,
        });
      }
    },
    [addIncidentComment, incidentId],
  );

  useEffect(() => {
    return closeModal();
  }, [closeModal]);

  useEffect(() => {
    if (isSuccess) {
      closeModal();
      reset();
      toast.success('Votre commentaire a été publié', {
        position: 'top-right',
      });
    }
  }, [isSuccess, reset, closeModal]);

  return (
    <Modal
      isOpen={showAddIncidentCommentModal}
      onRequestClose={closeModal}
      className="absolute bg-white py-6 px-8 border border-neutral-300 rounded-modal shadow-modal top-20 left-8 sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[600px] max-h-[90vh] overflow-y-auto"
      contentLabel="Show Add Incident Comment Modal"
      ariaHideApp={false}
    >
      <div className="flex items-center justify-center gap-2 mb-6">
        <MessageSquarePlus size={24} className="text-primary-600" />
        <h2 className="text-heading-md text-neutral-900">Ajouter un commentaire</h2>
      </div>

      <hr className="mb-6 border-neutral-200" />

      <form className="space-y-6">
        <StandardInput
          register={register}
          id="body"
          label="Commentaire *"
          type="textarea"
          placeholder="Commentaire"
          error={errors.body}
        />

        {!!templateMessages?.length && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={20} className="text-primary-600" />
              <p className="text-body-sm font-medium text-neutral-900">
                Utiliser un message type
              </p>
            </div>
            <SelectInput
              register={register}
              id="template"
              options={[{ id: '', label: 'Sélectionnez un message' }].concat(
                templateMessages.map((message) => ({
                  id: message.body,
                  label: message.title,
                })),
              )}
              callback={(message: string) => {
                setValue('body', message);
                setValue('template', '');
              }}
            />
          </div>
        )}
      </form>

      <div className="flex justify-end mt-6 pt-6 border-t border-neutral-200">
        <button
          className="px-6 py-3 rounded-button bg-primary-600 hover:bg-primary-700 text-white shadow-button transition-colors duration-200 text-body-sm font-medium"
          onClick={handleSubmit(onSubmit)}
        >
          Valider
        </button>
      </div>
    </Modal>
  );
};

export default AddIncidentCommentModal;
